import { applyPathParams } from '@/api/fetcher';
import { Page } from '@/components/layout/Page';
import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { PAGE_PERSONA } from '@/features/decisions/config/personaContext';
import { useActivePersonaContext } from '@/features/decisions/hooks/useActivePersonaContext';
import { useComparisonCandidates } from '@/features/decisions/hooks/useComparisonCandidates';
import { useFcfEntitlement } from '@/features/decisions/hooks/useFcfEntitlement';
import { computeOcfDeltas } from '@/features/decisions/utils/ocf';

import { ActivePersonaContextSettings } from '../canvas/ActivePersonaContextSettings';
import { PolicyChangesPanel } from '../policies/PolicyChangesPanel';
import { FcfBuildup } from './FcfBuildup';
import { FcfContributorsSection } from './FcfContributorsSection';
import { FcfImpactChart } from './FcfImpactChart';
import { ResultMetricsTable } from './ResultMetricsTable';
import { ResultSpecificationPanel } from './ResultSpecificationPanel';
import { ValueOpportunityKpis } from './ValueOpportunityKpis';

const LOST_SALES_PCT = 0;
const REGION = 'North America';

export function ValueOpportunityContainer() {
  const {
    scenarioRunId,
    runId,
    refExperimentId,
    experimentIdRaw: personaExperimentIdRaw,
    periodFrom,
    periodTo,
    keystoneOnlyOverride,
    overrideValue,
    chartMultiplier,
    setUrlParam,
    searchParams,
  } = useActivePersonaContext(PAGE_PERSONA.valueOpportunity);

  // This run's own top candidates replace PAGE_PERSONA's hardcoded default when experiment_id
  // hasn't been pinned explicitly — see useComparisonCandidates for why this isn't folded into
  // useActivePersonaContext itself.
  const { candidates, experimentIdRaw, experimentIds } = useComparisonCandidates({
    scenarioRunId,
    runId,
    experimentIdRaw: personaExperimentIdRaw,
    hasExplicitExperimentId: Boolean(searchParams.get('experiment_id')),
  });

  const entitlement = useFcfEntitlement({
    scenarioRunId,
    runId,
    refExperimentId,
    experimentIds,
    periodFrom,
    periodTo,
  });

  const rawFcfOpportunity = computeOcfDeltas(entitlement.refBar, entitlement.headlineCmp, LOST_SALES_PCT).ocf;
  const fcfOpportunity = rawFcfOpportunity != null ? rawFcfOpportunity * chartMultiplier : null;

  return (
    <Page
      subheader="Operating cash impact by planning policy"
      extraCrumbs={[
        { label: 'Backtest Results', href: RouterConfig[CanvasRoutes.DecisionsBacktests].path, clearSearch: true },
        {
          label: 'Backtest Scenario',
          href: applyPathParams(RouterConfig[CanvasRoutes.DecisionsBacktestDetail].path, { scenarioRunId }),
          clearSearch: true,
        },
        { label: 'Run Summary' },
      ]}
      actions={
        <ActivePersonaContextSettings
          scenarioRunId={scenarioRunId}
          runId={runId}
          refExperimentId={refExperimentId}
          experimentId={experimentIdRaw}
          experimentIds={experimentIds}
          candidates={candidates}
          periodFrom={periodFrom}
          periodTo={periodTo}
          keystoneOnlyOverride={keystoneOnlyOverride}
          overrideValue={overrideValue}
          chartMultiplier={chartMultiplier}
          onCommit={setUrlParam}
        />
      }
    >
      <div className="space-y-6">
        {periodFrom > periodTo && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            <span className="font-medium">Invalid date range:</span> Start date must be on or before end date.
          </div>
        )}
        {entitlement.error && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {entitlement.error.message.includes('no data') ? (
              'No simulation results found for the selected date range and experiments. Try adjusting the period or experiment IDs.'
            ) : (
              <>
                <span className="font-medium">/fcf-entitlement failed:</span> {entitlement.error.message}
              </>
            )}
          </div>
        )}

        <ResultSpecificationPanel
          cards={entitlement.cards}
          region={REGION}
          periodFrom={periodFrom}
          periodTo={periodTo}
        />

        <ResultMetricsTable refBar={entitlement.refBar} cmpBars={entitlement.cmpBars} />

        <FcfContributorsSection
          scenarioRunId={scenarioRunId}
          runId={runId}
          refExperimentId={refExperimentId}
          experimentId={experimentIds[0] ?? ''}
          linkExperimentIdRaw={experimentIdRaw}
          periodFrom={periodFrom}
          periodTo={periodTo}
        />

        <ValueOpportunityKpis
          cards={entitlement.cards}
          fcfOpportunity={fcfOpportunity}
          lostSalesPct={LOST_SALES_PCT}
          periodFrom={periodFrom}
          periodTo={periodTo}
          chartMultiplier={chartMultiplier}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-2 lg:col-span-2">
            <FcfImpactChart
              refBar={entitlement.refBar}
              cmpBars={entitlement.cmpBars}
              cards={entitlement.cards}
              periodFrom={periodFrom}
              periodTo={periodTo}
              lostSalesPct={LOST_SALES_PCT}
              keystoneOnlyOverride={keystoneOnlyOverride}
              overrideValue={overrideValue}
              chartMultiplier={chartMultiplier}
            />
            <PolicyChangesPanel experimentIds={[refExperimentId, ...experimentIds]} />
          </div>
          <div className="lg:col-span-1">
            <FcfBuildup
              refBar={entitlement.refBar}
              headlineCmp={entitlement.headlineCmp}
              deltas={entitlement.deltas}
              lostSalesPct={LOST_SALES_PCT}
              chartMultiplier={chartMultiplier}
            />
          </div>
        </div>
      </div>
    </Page>
  );
}
